/* eslint-disable max-lines */
import {
    MessageState,
    SendProps,
    SendRefType,
} from './types'
import {
    NinchatMessages,
    NinchatProperties,
    NinchatState,
} from 'api/types'
import React, {
    Ref, useContext,
    useState,
} from 'react'
import {
    UiState,
    sendFile,
    uiSlice,
} from 'state/ui'
import {
    useAppDispatch,
    useAppSelector,
    useAppSelectorWithShallowEquality,
} from 'hooks'
import {AttachmentFile} from '../../types/ui'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import EmbedNamePromiseModal from 'components/Embed/Modal/EmbedNamePromiseModal'
import {SavedNote} from '../SendNotes/types'
import SendView from './SendView'
import {UiStore} from 'store/types'

import {config} from 'embed/listeners'
import {sendMessage} from 'api/action/sendMessage'
import {shouldShowAttachment} from 'utils/channels'
import {stripMarkdown} from 'utils/markdown'
import {toast} from '../Toast'
import {usePromiseModal} from 'components/Modal/PromiseModal/hooks/usePromiseModal'
import {useSendToParent} from 'embed/utils/event'
import {useTags} from 'hooks/ninchat'
import {useTranslation} from 'react-i18next'
import {useWritingIndicator} from 'hooks/writingindicator'


export const {saveMessageState} = uiSlice.actions

const Send = React.forwardRef((props: SendProps, ref: Ref<SendRefType>): React.ReactElement => {
    const dispatch = useAppDispatch()
    const {t} = useTranslation()
    const targetOrigin = config.get('targetOrigin') || ''
    const sendToParent = useSendToParent()

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const [pastMessages, setPastMessages] = useState<Array<{
        text: string,
        time: number,
    }>>([])


    const {
        isAudienceChannel,
        chat,
        features,
        realm_id,
    } = props

    const openEmbedModal = usePromiseModal(EmbedNamePromiseModal, {instanceId: 'embedName'})
    const isAdmin = useAppSelector(({ninchat}) => !!ninchat.user_attrs.admin)
    const userName = useAppSelector(({ninchat}) => !!ninchat.user_attrs.name)

    const chat_id = chat.channel_id ?? chat.user_id

    const isSilenced = useAppSelector(({ninchat}) => ninchat?.user_channels?.[chat.channel_id || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs ?? {}).silenced || false

    const {files}: {files: UiStore.UiStoreAttachmentFile[] | undefined} = useAppSelectorWithShallowEquality(({ui}: {ui: UiState}) => ({files: ui.file_uploads?.[chat_id]}))
    const message_state = useAppSelector(({ui}) => ui.message_state?.[chat_id])

    const uploadRole = useAppSelector(({ninchat}) => (chat.channel_id ? ninchat.user_channels?.[chat.channel_id]?.channel_attrs?.upload : undefined))
    // eslint-disable-next-line max-len
    const currentUserIsModerator = useAppSelector(({ninchat}) => (chat.channel_id ? ninchat.user_channels[chat.channel_id || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.moderator : false))
    // eslint-disable-next-line max-len
    const currentUserIsOperator = useAppSelector(({ninchat}) => (chat.channel_id ? ninchat.user_channels[chat.channel_id || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.operator : false))

    const memberMessageMetadata = useAppSelector(({ninchat}) => {
        if (!chat.channel_id) {
            return {} as NinchatProperties.NinchatMemberMessageMetadataContents
        }

        return ninchat.user_channels[chat.channel_id || '']?.member_message_metadata?.[ninchat.user_id || ''] ?? {}
    })

    const defaultMessageState: MessageState = {
        chat,
        files: [],
        message: '',
    }

    const showAttachment = shouldShowAttachment({
        isAdmin,
        isDialogue: !!chat.user_id,
        isModerator: currentUserIsModerator ?? false,
        isOperator: currentUserIsOperator ?? false,
        upload: uploadRole,
    })


    const [messageState, setMessageState] = React.useState(defaultMessageState)

    const lastMessageState = React.useRef(messageState)

    React.useEffect(() => {
        setMessageState({
            ...messageState,
            files: (files ?? []) as AttachmentFile[],
        })
    }, [files])

    React.useEffect(() => {
        lastMessageState.current = messageState
    }, [messageState])

    React.useEffect(() => {
        setMessageState({
            ...defaultMessageState,
            ...message_state,
        })

        return (): void => {
            dispatch(saveMessageState({
                chat: lastMessageState.current.chat,
                files: lastMessageState.current.files,
                message: lastMessageState.current.message,
            }))
        }
    }, [chat_id])

    const {
        userId, idle,
    } = useAppSelectorWithShallowEquality(({ninchat}: { ninchat: NinchatState }) => ({
        idle: ninchat.user_attrs.idle,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        userId: ninchat.user_id!,
    }))

    const tags = useTags(features?.tags ? realm_id : undefined)

    const [startWriting, stopWriting] = useWritingIndicator({
        channel_id: chat.channel_id,
        idle,
        user_id: userId,
    })

    React.useEffect(() => {
        // todo(pallab) current only send server notification for audience channel. implement for others later
        if (!isAudienceChannel) {
            return
        }
        if (messageState.message.length > 0) {
            startWriting()
        } else {
            stopWriting()
        }
    }, [messageState.message.length])

    const send = () => {
        const {message} = messageState
        const newFiles = messageState.files.filter((file) => file instanceof File)

        if (message.trim().length === 0) {
            setMessageState(defaultMessageState)
        } else {
            try {
                let messageToSend = message.trim()
                if (isEmbedQueueChannel) {
                    messageToSend = stripMarkdown(messageToSend)
                }

                dispatch(sendMessage({
                    ...chat,
                    message_type: NinchatMessages.NinchatMessageTypes.NinchatTextMessage,
                    payload: [JSON.stringify({text: messageToSend})],
                })).unwrap()
                    .then(() => {
                        if (isEmbedQueueChannel) {
                            sendToParent({
                                channel_id: chat.channel_id || '',
                                event: EmbedEvent.MessageSent,
                                targetOrigin,
                            })

                            sendToParent({
                                event: EmbedEvent.EmbedSound,
                                sound: 'messageSent',
                                targetOrigin,
                            })

                            const textarea = document.querySelector<HTMLElement>('[data-id=\'send-message-textarea\']')
                            textarea?.focus()

                            setPastMessages((prevMessages) => [...prevMessages, {
                                text: messageToSend,
                                time: new Date().getTime(),
                            }])
                        }
                    })
                    .catch(() => undefined)

                setMessageState((currMessage) => ({
                    ...currMessage,
                    message: '',
                }))
            } catch {} // eslint-disable-line no-empty
        }

        if (newFiles.length) {
            dispatch(sendFile({
                chat,
                files: newFiles,
            }))
            setMessageState((currMessage) => ({
                ...currMessage,
                files: [...currMessage.files],
            }))
        }
    }

    const handleSend = (): void => {
        if (isEmbedQueueChannel && embed.askUserName && !userName) {
            openEmbedModal({title: t('Enter your name to send the message')}).then(
                () => {
                    send()
                },
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                () => {},
            )
        } else {
            send()
        }
    }

    const onSaveNotes = (notes: SavedNote[]): void => {
        const data = {
            ...memberMessageMetadata.tag_ids && {tag_ids: memberMessageMetadata.tag_ids},
            Notes: Object.keys(notes).length === 0 ? null : notes, // Delete Notes if empty
        }

        dispatch(sendMessage({
            ...chat,
            message_fold: true,
            message_privileged: true,
            message_type: NinchatMessages.NinchatMessageTypes.NinchatMetadataMessage,
            payload: [JSON.stringify({data})],
        }))
            .unwrap()
            .then(
                () => {
                    toast.success(t('Notes saved'))
                },
                () => {
                    toast.error(t('Error saving notes'))
                },
            )
    }

    const onSaveTags = (tagIds: NinchatProperties.TagID[]): void => {
        const data = {
            ...memberMessageMetadata.Notes && {Notes: memberMessageMetadata.Notes},
            tag_ids: tagIds.length === 0 ? null : tagIds, // Delete tag_ids if empty
        }

        dispatch(sendMessage({
            ...chat,
            message_fold: true,
            message_privileged: true,
            message_type: NinchatMessages.NinchatMessageTypes.NinchatMetadataMessage,
            payload: [JSON.stringify({data})],
        }))
            .unwrap()
            .then(
                () => {
                    toast.success(t('Tags saved'))
                },
                () => {
                    toast.error(t('Error saving tags'))
                },
            )
    }

    return (
        <SendView
            chat_id={chat_id}
            handleSend={handleSend}
            isSilenced={isSilenced}
            memberMessageMetadata={memberMessageMetadata}
            messageState={messageState}
            onSaveNotes={onSaveNotes}
            onSaveTags={onSaveTags}
            pastMessages={pastMessages}
            ref={ref}
            setMessageState={setMessageState}
            showAttachment={showAttachment}
            tags={tags}
            {...props}
        />
    )
})

Send.displayName = 'Send'

export default React.memo(Send)
